export type LifecycleStage = 'yet_transferred' | 'warranty' | 'watch' | 'maintenance';

export const LIFECYCLE_STAGE_LABELS: Record<LifecycleStage, string> = {
  yet_transferred: 'Chưa bàn giao',
  warranty: 'Bảo hành',
  watch: 'Theo dõi',
  maintenance: 'Bảo trì',
};

export const LIFECYCLE_STAGE_COLORS: Record<LifecycleStage, string> = {
  yet_transferred: 'bg-gray-50 text-gray-700 border-gray-300 dark:bg-gray-900/30 dark:text-gray-300 dark:border-gray-700',
  warranty: 'bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-800',
  watch: 'bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800',
  maintenance: 'bg-purple-50 text-purple-700 border-purple-300 dark:bg-purple-950/30 dark:text-purple-300 dark:border-purple-800',
};

// ───────────────────────────────────────────────────────────────
// Build lifecycle (status='build', manual only)
// ───────────────────────────────────────────────────────────────
export type LifecycleStageBuild =
  | 'm1_open'
  | 'm2_structural'
  | 'm3_mep'
  | 'm4_finishing'
  | 'm5_acceptance';

export const LIFECYCLE_STAGE_BUILD_LABELS: Record<LifecycleStageBuild, string> = {
  m1_open: 'B1 Mở CT',
  m2_structural: 'B2 Phần Thô',
  m3_mep: 'B3 M&E',
  m4_finishing: 'B4 Hoàn thiện',
  m5_acceptance: 'B5 Nghiệm thu',
};

export const LIFECYCLE_STAGE_BUILD_COLORS: Record<LifecycleStageBuild, string> = {
  m1_open: 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-900/30 dark:text-gray-300 dark:border-gray-700',
  m2_structural: 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-800',
  m3_mep: 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800',
  m4_finishing: 'bg-green-100 text-green-700 border-green-300 dark:bg-green-950/30 dark:text-green-300 dark:border-green-800',
  m5_acceptance: 'bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-950/30 dark:text-purple-300 dark:border-purple-800',
};

export const LIFECYCLE_STAGE_BUILD_OPTIONS = [
  { value: null, label: 'Chưa set' },
  { value: 'm1_open', label: 'B1 Mở CT' },
  { value: 'm2_structural', label: 'B2 Phần Thô' },
  { value: 'm3_mep', label: 'B3 M&E' },
  { value: 'm4_finishing', label: 'B4 Hoàn thiện' },
  { value: 'm5_acceptance', label: 'B5 Nghiệm thu' },
] as const;

// ───────────────────────────────────────────────────────────────
// Tender lifecycle (status='tender', manual only)
// ───────────────────────────────────────────────────────────────
export type LifecycleStageTender =
  | 't1_orientation'
  | 't2_submitted'
  | 't3_negotiation';

export const LIFECYCLE_STAGE_TENDER_LABELS: Record<LifecycleStageTender, string> = {
  t1_orientation: 'T1 Định hướng',
  t2_submitted: 'T2 Đã submit',
  t3_negotiation: 'T3 Thương lượng',
};

export const LIFECYCLE_STAGE_TENDER_COLORS: Record<LifecycleStageTender, string> = {
  t1_orientation: 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-900/30 dark:text-gray-300 dark:border-gray-700',
  t2_submitted: 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-800',
  t3_negotiation: 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800',
};

export const LIFECYCLE_STAGE_TENDER_OPTIONS = [
  { value: null, label: 'Chưa set' },
  { value: 't1_orientation', label: 'T1 Định hướng' },
  { value: 't2_submitted', label: 'T2 Đã submit' },
  { value: 't3_negotiation', label: 'T3 Thương lượng' },
] as const;

// ───────────────────────────────────────────────────────────────
// Watch substatus (status='watch' within service, manual only)
// ───────────────────────────────────────────────────────────────
export type LifecycleWatchSubstatus =
  | 'theo_doi'
  | 'lien_he'
  | 'thuong_luong'
  | 'thi_cong_ps';

export const LIFECYCLE_WATCH_SUBSTATUS_LABELS: Record<LifecycleWatchSubstatus, string> = {
  theo_doi: 'Đang theo dõi',
  lien_he: 'Liên hệ',
  thuong_luong: 'Thương lượng',
  thi_cong_ps: 'Thi công PS',
};

export const LIFECYCLE_WATCH_SUBSTATUS_COLORS: Record<LifecycleWatchSubstatus, string> = {
  theo_doi: 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-900/30 dark:text-gray-300 dark:border-gray-700',
  lien_he: 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-800',
  thuong_luong: 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800',
  thi_cong_ps: 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800',
};

export const LIFECYCLE_WATCH_SUBSTATUS_OPTIONS = [
  { value: 'theo_doi', label: 'Đang theo dõi' },
  { value: 'lien_he', label: 'Liên hệ' },
  { value: 'thuong_luong', label: 'Thương lượng' },
  { value: 'thi_cong_ps', label: 'Thi công PS' },
] as const;

